import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDisableConnectionsOptions,
  BulkDisableConnectionsResponse,
  BulkEnableConnectionsOptions,
  BulkEnableConnectionsResponse,
  Connection,
  ConnectionCreatedEnvelope,
  ConnectionDeletedEnvelope,
  ConnectionUpdatedEnvelope,
  GetEnabledConnectionResponse,
  ListConnectionsOptions,
  ListConnectionsResponse,
  ListEnabledConnectionsClientDataOptions,
  ListEnabledConnectionsClientDataResponse,
  UpdateConnection,
  bulkDisableConnections as universalBulkDisableConnections,
  bulkEnableConnections as universalBulkEnableConnections,
  createConnection as universalCreateConnection,
  deleteConnection as universalDeleteConnection,
  disableConnection as universalDisableConnection,
  enableConnection as universalEnableConnection,
  getConnection as universalGetConnection,
  getEnabledConnection as universalGetEnabledConnection,
  listConnections as universalListConnections,
  listEnabledConnectionsClientData as universalListEnabledConnectionsClientData,
  updateConnection as universalUpdateConnection,
} from './iam-connection-v1-connection-connection.universal.js';

const TRANSFORM_PATHS_0 = [
  { path: 'entity.createdDate' },
  { path: 'entity.updatedDate' },
  { path: 'metadata.eventTime' },
];
const TRANSFORM_PATHS_1 = [
  { path: 'undefined.createdDate' },
  { path: 'undefined.updatedDate' },
  { path: 'metadata.eventTime' },
];
export const __metadata = { PACKAGE_NAME: '@wix/identity' };

export function createConnection(
  httpClient: HttpClient
): CreateConnectionSignature {
  return (connection: Connection) =>
    universalCreateConnection(
      connection,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateConnectionSignature {
  /**
   * Creates an authentication connection.
   *
   * This defines and configures the connection but does not activate it.
   * To make the connection available for user authentication, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
   * @param - Connection to create.
   * @returns Created connection.
   */
  (connection: Connection): Promise<
    NonNullablePaths<Connection, `appDefId` | `displayName`, 0>
  >;
}

export function updateConnection(
  httpClient: HttpClient
): UpdateConnectionSignature {
  return (
    _id: string,
    connection: NonNullablePaths<UpdateConnection, `revision`, 0>
  ) =>
    universalUpdateConnection(
      _id,
      connection,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateConnectionSignature {
  /**
   * Updates a connection's configuration.
   *
   * Each time the connection is updated, `revision` increments by 1. The current `revision` must be passed when updating the connection. This ensures you're working with the latest connection and prevents unintended overwrites.
   *
   * You can update connection settings whether the connection is enabled or disabled.
   * Changes take effect immediately for enabled connections.
   * @param - Connection ID.
   * @returns Updated connection.
   */
  (
    _id: string,
    connection: NonNullablePaths<UpdateConnection, `revision`, 0>
  ): Promise<NonNullablePaths<Connection, `appDefId` | `displayName`, 0>>;
}

export function deleteConnection(
  httpClient: HttpClient
): DeleteConnectionSignature {
  return (connectionId: string) =>
    universalDeleteConnection(
      connectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteConnectionSignature {
  /**
   * Permanently deletes a connection.
   *
   * This removes the connection configuration entirely. If the connection is currently enabled,
   * it's automatically disabled before deletion. This action can't be undone.
   * @param - Connection ID.
   */
  (connectionId: string): Promise<void>;
}

export function enableConnection(
  httpClient: HttpClient
): EnableConnectionSignature {
  return (connectionId: string) =>
    universalEnableConnection(
      connectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface EnableConnectionSignature {
  /**
   * Enables a connection for user authentication.
   *
   * This activates a previously created connection, making it available for users to sign in with.
   * Calling this method for a connection that's already enabled succeeds, and the connection stays enabled.
   * To deactivate the connection, call [Disable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/disable-connection).
   *
   * > **Note:** The connection ID isn't validated. If no connection exists with the specified ID, the call still succeeds,
   * > but no sign-in option is added. To verify that the connection was enabled, call
   * > [List Enabled Connections Client Data](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/list-enabled-connections-client-data)
   * > and check that the connection appears in the response.
   * @param - Connection ID.
   */
  (connectionId: string): Promise<void>;
}

export function disableConnection(
  httpClient: HttpClient
): DisableConnectionSignature {
  return (connectionId: string) =>
    universalDisableConnection(
      connectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DisableConnectionSignature {
  /**
   * Disables a connection from user authentication.
   *
   * This deactivates the connection without deleting its configuration.
   * Users can no longer sign in using this connection until it's re-enabled.
   * To reactivate the connection, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
   * @param - Connection ID.
   */
  (connectionId: string): Promise<void>;
}

export function getConnection(httpClient: HttpClient): GetConnectionSignature {
  return (connectionId: string) =>
    universalGetConnection(
      connectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetConnectionSignature {
  /**
   * Retrieves a connection by ID.
   *
   * Returns the connection configuration regardless of whether it's enabled or disabled. To retrieve only enabled connections, call [Get Enabled Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/get-enabled-connection).
   * @param - Connection ID.
   * @returns Retrieved connection.
   */
  (connectionId: string): Promise<
    NonNullablePaths<Connection, `appDefId` | `displayName`, 0>
  >;
}

export function getEnabledConnection(
  httpClient: HttpClient
): GetEnabledConnectionSignature {
  return (connectionId: string) =>
    universalGetEnabledConnection(
      connectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEnabledConnectionSignature {
  /**
   * Retrieves an enabled connection by ID.
   *
   * Returns the connection only if it's currently enabled. Returns an error if the connection
   * exists but is disabled.
   * @param - Connection ID.
   */
  (connectionId: string): Promise<
    NonNullablePaths<
      GetEnabledConnectionResponse,
      `connection.appDefId` | `connection.displayName`,
      1
    >
  >;
}

export function listConnections(
  httpClient: HttpClient
): ListConnectionsSignature {
  return (options?: ListConnectionsOptions) =>
    universalListConnections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListConnectionsSignature {
  /**
   * Lists all connections with their enabled status.
   *
   * Returns both enabled and disabled connections, each with an `enabled` flag indicating
   * whether the connection is currently active for user authentication. The response includes
   * the site's built-in connections, in addition to any connections you create.
   */
  (options?: ListConnectionsOptions): Promise<
    NonNullablePaths<
      ListConnectionsResponse,
      | `connections`
      | `connections.${number}.connection.appDefId`
      | `connections.${number}.connection.displayName`
      | `connections.${number}.enabled`,
      3
    >
  >;
}

export function listEnabledConnectionsClientData(
  httpClient: HttpClient
): ListEnabledConnectionsClientDataSignature {
  return (options?: ListEnabledConnectionsClientDataOptions) =>
    universalListEnabledConnectionsClientData(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEnabledConnectionsClientDataSignature {
  /**
   * Lists enabled connections with client-safe data only.
   *
   * Returns only enabled connections with a filtered data structure suitable for client-side
   * consumption, excluding sensitive server-side configuration details. The response includes
   * the site's built-in connections, in addition to any connections you create.
   */
  (options?: ListEnabledConnectionsClientDataOptions): Promise<
    NonNullablePaths<
      ListEnabledConnectionsClientDataResponse,
      | `connections`
      | `connections.${number}._id`
      | `connections.${number}.appDefId`
      | `connections.${number}.displayName`,
      2
    >
  >;
}

export function bulkEnableConnections(
  httpClient: HttpClient
): BulkEnableConnectionsSignature {
  return (options?: BulkEnableConnectionsOptions) =>
    universalBulkEnableConnections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkEnableConnectionsSignature {
  /**
   * Enables multiple connections in a single request.
   *
   * Activates up to 100 connections for user authentication. Each connection must already
   * exist and be properly configured.
   * To enable a single connection, call [Enable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/enable-connection).
   */
  (options?: BulkEnableConnectionsOptions): Promise<
    NonNullablePaths<
      BulkEnableConnectionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export function bulkDisableConnections(
  httpClient: HttpClient
): BulkDisableConnectionsSignature {
  return (options?: BulkDisableConnectionsOptions) =>
    universalBulkDisableConnections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDisableConnectionsSignature {
  /**
   * Disables multiple connections in a single request.
   *
   * Deactivates up to 100 connections without deleting their configurations.
   * Users can no longer sign in using these connections until they're re-enabled.
   * To disable a single connection, call [Disable Connection](https://dev.wix.com/docs/api-reference/wix-backoffice/identity/connection-v1/disable-connection).
   */
  (options?: BulkDisableConnectionsOptions): Promise<
    NonNullablePaths<
      BulkDisableConnectionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export const onConnectionCreated = /*#__PURE__*/ EventDefinition(
  'wix.iam.connection.v1.connection_created',
  true,
  (event: ConnectionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: TRANSFORM_PATHS_0,
        },
      ])
    )
)<ConnectionCreatedEnvelope>();
export const onConnectionDeleted = /*#__PURE__*/ EventDefinition(
  'wix.iam.connection.v1.connection_deleted',
  true,
  (event: ConnectionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: TRANSFORM_PATHS_1,
        },
      ])
    )
)<ConnectionDeletedEnvelope>();
export const onConnectionUpdated = /*#__PURE__*/ EventDefinition(
  'wix.iam.connection.v1.connection_updated',
  true,
  (event: ConnectionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: TRANSFORM_PATHS_0,
        },
      ])
    )
)<ConnectionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  Asset,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkDisableConnectionsOptions,
  BulkDisableConnectionsRequest,
  BulkDisableConnectionsResponse,
  BulkEnableConnectionsOptions,
  BulkEnableConnectionsRequest,
  BulkEnableConnectionsResponse,
  BulkEnabledConnectionPropertiesResult,
  Connection,
  ConnectionClientData,
  ConnectionCreatedEnvelope,
  ConnectionDeletedEnvelope,
  ConnectionUpdatedEnvelope,
  ConnectionWithEnabled,
  CreateConnectionRequest,
  CreateConnectionResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteConnectionRequest,
  DeleteConnectionResponse,
  DeleteContext,
  DeleteStatus,
  DisableConnectionRequest,
  DisableConnectionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EnableConnectionRequest,
  EnableConnectionResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetConnectionRequest,
  GetConnectionResponse,
  GetEnabledConnectionRequest,
  GetEnabledConnectionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListConnectionsOptions,
  ListConnectionsRequest,
  ListConnectionsResponse,
  ListEnabledConnectionsClientDataOptions,
  ListEnabledConnectionsClientDataRequest,
  ListEnabledConnectionsClientDataResponse,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UpdateConnection,
  UpdateConnectionRequest,
  UpdateConnectionResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './iam-connection-v1-connection-connection.universal.js';
